import React, { FC, useState } from 'react';
import {
  Button,
  ButtonColor,
  Loader,
  NavigationLeftSection,
  NavigationRightSection,
  useAccountName,
} from '@sapiens/insured-component-library';
import {
  AccountDropdownContainer,
  AccountDropdownItem,
  HeaderAccountIcon,
  HeaderAccountSection,
  HeaderLink,
  HeaderLinkContainer,
  HeaderLinkSection,
  HeaderLogoContainer,
  HeaderNavigation,
  SeeProfileLink,
  FileClaimLinkButton,
  StyledLoader,
} from './header.styles';
import { useRouter } from 'next/router';
import { HEADER_NAVIGATION_LINKS } from 'common/utils/constants/header.constants';
import { Typography } from 'common/components/typography/typography';
import { useTheme } from 'styled-components';
import { FontType } from 'common/utils/constants/typography.constants';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import { handleLogout } from 'modules/auth/utils/helpers/auth.helpers';
import { useCustomerInformation } from 'modules/user-profile/hooks/use-user-details';
import { FlexBox } from 'common/utils/styles/flex-box';
import Error from 'common/components/error/errors';
import { useUserStore } from 'common/stores/user-store';
import { LeaveConfirmation } from '../leave-confirmation/leave-confirmation';
import { NavRouteParams } from '../leave-confirmation/leave-confirmation.model';
import { FileClaimPopup } from '../file-claim-popup/file-claim-popup';

export interface HeaderProps {
  unSavedChangesExistInJourney: number;
  unSavedChangesExistInFnol: number;
  onBoardedUser: boolean;
}

export const Header: React.FC<HeaderProps> = ({
  unSavedChangesExistInJourney,
  unSavedChangesExistInFnol,
  onBoardedUser,
}) => {
  const router = useRouter();
  const theme = useTheme();
  const { t } = useTranslation();

  const [showQuotePopup, setShowQuotePopup] = useState(false); // Separate state for QUOTE popup
  const [showClaimPopup, setShowClaimPopup] = useState(false);
  const [fileClaimPopup, setFileClaimPopup] = useState(false);
  const [nxtRoute, setNxtRoute] = useState<NavRouteParams>({});

  // Handle all anchor tag links(including on profile link click)
  const checkForUnsavedChanges = (event: any, path: string) => {
    let hasUnsavedChanges = false;
    if (unSavedChangesExistInJourney === 1) {
      event.preventDefault();
      setShowQuotePopup(true);
      hasUnsavedChanges = true;
    } else if (unSavedChangesExistInFnol === 1) {
      event.preventDefault();
      setShowClaimPopup(true);
      hasUnsavedChanges = true;
    }
    if (hasUnsavedChanges) {
      setNxtRoute({ path: path });
      return false;
    }

    return true;
  };

  //Handle Profile button, report claim and logout button clicks
  const onButtonClick = (pathname: string) => {
    if (pathname === 'logout') {
      if (unSavedChangesExistInJourney === 1) {
        setShowQuotePopup(true);
      }
      if (unSavedChangesExistInFnol === 1) {
        setShowClaimPopup(true);
      }
      if (unSavedChangesExistInJourney === 1 || unSavedChangesExistInFnol === 1) {
        setNxtRoute({ logout: true });
        return false;
      }

      handleLogout();
    } else if (pathname === '/profile' || pathname === '/dashboard') {
      if (unSavedChangesExistInJourney === 1) {
        setShowQuotePopup(true);
      }
      if (unSavedChangesExistInFnol === 1) {
        setShowClaimPopup(true);
      }

      if (unSavedChangesExistInJourney === 1 || unSavedChangesExistInFnol === 1) {
        setNxtRoute({ path: pathname });
        return false;
      }

      router.push(pathname);
    } else if (pathname === '/policies') {
      if (unSavedChangesExistInJourney === 1) {
        setShowQuotePopup(true);
      }
      if (unSavedChangesExistInFnol === 1) {
        setShowClaimPopup(true);
      }

      if (unSavedChangesExistInJourney === 1 || unSavedChangesExistInFnol === 1) {
        setNxtRoute({ path: pathname, helpParam: true });
        return false;
      }

      router.push({ pathname: pathname, query: { help: true } });
    }
  };

  const FILTERED_HEADER_LINKS = onBoardedUser
    ? HEADER_NAVIGATION_LINKS.filter(({ label }) => label === 'quotes')
    : HEADER_NAVIGATION_LINKS;

  return (
    <>
      <HeaderNavigation>
        <NavigationLeftSection>
          <HeaderLogoContainer
            href={onBoardedUser ? '/quotes' : '/dashboard'}
            onClick={(e) => checkForUnsavedChanges(e, onBoardedUser ? '/quotes' : '/dashboard')}
          >
            <Image src={'/icons/logo.svg'} alt="start-date" width="184" height="33" />
          </HeaderLogoContainer>

          <HeaderLinkSection>
            {FILTERED_HEADER_LINKS.map(({ label, path, matcher }) => (
              <HeaderLink
                key={`${label}-${path}`}
                href={path}
                isActive={Boolean(router.pathname.match(matcher))}
                onClick={(e) => checkForUnsavedChanges(e, path)}
              >
                <Typography fontType={FontType.COPY_SMALL_BOLD} color={theme.palette.neutral.white}>
                  {t(`navigationLinks.${label}`)}
                </Typography>
              </HeaderLink>
            ))}
          </HeaderLinkSection>
        </NavigationLeftSection>

        <NavigationRightSection>
          {!onBoardedUser && (
            <HeaderLinkContainer
              onClick={(e) => {
                const allowPopup = checkForUnsavedChanges(e, '');
                if (allowPopup) {
                  setFileClaimPopup(true);
                } else {
                  setNxtRoute({ path: '', showFileClaimAfter: true });
                }
              }}
            >
              <Image src={'/icons/header-file-claim.svg'} alt="start-date" width="24" height="32" />
              <FileClaimLinkButton>
                <Typography fontType={FontType.COPY_SMALL_BOLD} color={theme.palette.neutral.black}>
                  {t('header.fileAClaim')}
                </Typography>
              </FileClaimLinkButton>
            </HeaderLinkContainer>
          )}
          <HeaderAccount onViewProfileClick={checkForUnsavedChanges} onProfileLogoutClick={onButtonClick} />
        </NavigationRightSection>
      </HeaderNavigation>
      <LeaveConfirmation
        showQuotePopup={showQuotePopup}
        setQuoteShowPopup={setShowQuotePopup}
        showClaimPopup={showClaimPopup}
        setClaimShowPopup={setShowClaimPopup}
        nxtRoute={nxtRoute}
        setFileClaimPopup={setFileClaimPopup}
      />
      <FileClaimPopup showPopup={fileClaimPopup} setShowPopup={setFileClaimPopup} />
    </>
  );
};

export interface HeaderAccountProps {
  onViewProfileClick: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>, pathname: string) => void;
  onProfileLogoutClick: (pathname: string) => void;
}

export const HeaderAccount: FC<HeaderAccountProps> = ({ onViewProfileClick, onProfileLogoutClick }) => {
  const { data, isError, isLoading, error } = useCustomerInformation();

  const setUserDetail = useUserStore((state) => state.setUserDetail);

  if (isLoading) {
    return (
      <FlexBox flexType="container" justify="center" align="center">
        <StyledLoader />
      </FlexBox>
    );
  }

  if (isError || !data || !data.content) {
    return <Error errorMessage={error?.message} />;
  }

  setUserDetail(data);
  const { name } = data.content;

  return (
    <HeaderAccountSection>
      <AccountDropdown
        defaultValue={name}
        clicked={() => onProfileLogoutClick('logout')}
        onViewProfileClick={onViewProfileClick}
      />
      <AccountIconName name={name} onProfileLogoutClick={onProfileLogoutClick} />
    </HeaderAccountSection>
  );
};

interface AccountIconNameProps {
  name: string;
  onProfileLogoutClick: (pathname: string) => void;
}

const AccountIconName: FC<AccountIconNameProps> = ({ name, onProfileLogoutClick }) => {
  const theme = useTheme();

  return (
    <HeaderAccountIcon onClick={() => onProfileLogoutClick('/profile')} color={theme.palette.neutral.white}>
      {useAccountName(name)}
    </HeaderAccountIcon>
  );
};

interface AccountDropdownProps {
  clicked: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  defaultValue: string;
  onViewProfileClick: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>, pathname: string) => void;
}

const AccountDropdown: React.FC<AccountDropdownProps> = ({ defaultValue, clicked, onViewProfileClick }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <AccountDropdownContainer
      value={
        (
          <Typography
            className="account-dropdown-display-name"
            color={theme.palette.neutral.white}
            fontType={FontType.COPY_LARGE}
          >
            {defaultValue}
          </Typography>
        ) as unknown as string
      }
      dropdownClassName="account-dropdown"
    >
      <AccountDropdownItem value={'1'} className="account-dropdown-option">
        <SeeProfileLink>
          <HeaderLink href="/profile" onClick={(e) => onViewProfileClick(e, '/profile')}>
            {t('header.seeProfile')}
          </HeaderLink>
        </SeeProfileLink>
      </AccountDropdownItem>
      <AccountDropdownItem value={'2'} className="account-dropdown-option">
        <Button color={ButtonColor.primary} onClick={clicked}>
          <Typography fontType={FontType.COPY_SMALL_BOLD} color={theme.palette.neutral.white}>
            {t('header.logout')}
          </Typography>
        </Button>
      </AccountDropdownItem>
    </AccountDropdownContainer>
  );
};
